import _ from "lodash";
import client from "lib/apolloClient/client";
import { transformStudentPersonalDetailsToBackendFormat } from "./utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  updateStudentPersonalDetailsMutation,
  upsertTranscriptStudentCommentsMutation,
  updateTranscriptStudentGradeCourseMappingMutation,
  upsertTranscriptStudentRatingsMutation,
  updateTranscriptGradeScalesMutation,
  syncPlatformGradeScaleForTranscriptsMutation,
  bulkCopyTranscriptConfigurationMutation,
  bulkCopyTranscriptGradingPeriodsMutation,
  updateTranscriptConfigurationMutation,
  updateTranscriptStudentGradeGradingPeriodMappingMutation,
  updateTranscriptGradingPeriodsMutation,
} from "./AcademicRecordsMutations";
import {
  getOrgTranscriptEntitiesQuery,
  getStudentTranscriptAcademicRecordsQuery,
  getTranscriptResourcesQuery,
} from "./AcademicRecordsQueries";
import { calculateNextSequence } from "./helpers/displaySequenceHelpers";
import { parseCompositeKey } from "./helpers/commonUtils";

// Helper functions for mutation data transformation
const extractRemovedCourses = deletedRecordRows => {
  return _.flatMap(deletedRecordRows, studentRecords =>
    _.reduce(
      studentRecords,
      (acc, isDeleted, compositeKey) => {
        if (isDeleted) {
          const [studentGradeMapId, courseId] = parseCompositeKey(compositeKey);
          acc.push({ studentGradeMapId, courseId });
        }
        return acc;
      },
      []
    )
  );
};

const getDeletedCourseIdsForGradeMap = (
  deletedRecordRows,
  studentId,
  gradeMapId
) => {
  const studentRecords = _.get(deletedRecordRows, studentId, {});
  return _.reduce(
    studentRecords,
    (acc, isDeleted, compositeKey) => {
      if (isDeleted) {
        const [delGradeMapId, courseId] = parseCompositeKey(compositeKey);
        if (delGradeMapId === gradeMapId) {
          acc.push(courseId);
        }
      }
      return acc;
    },
    []
  );
};

const processNewCourseRowsForGradeMap = ({
  gradeMapRows,
  gradeMapId,
  academicRecord,
  deletedCourseIds,
  added,
}) => {
  const existingCourses = academicRecord
    ? _.filter(
        academicRecord.courses,
        courseItem => !_.includes(deletedCourseIds, courseItem.course.id)
      )
    : [];

  let nextDisplaySequence = calculateNextSequence(
    existingCourses,
    added,
    gradeMapId
  );

  _.forEach(gradeMapRows, rowData => {
    const { courseId, levelId, courseCode } = rowData;
    if (courseId) {
      added.push({
        studentGradeMapId: gradeMapId,
        courseId,
        ...(levelId && { levelId }),
        ...(courseCode && { courseCode }),
        displaySequence: nextDisplaySequence,
      });
      nextDisplaySequence += 1;
    }
  });
};

const processUpdatedCourseLevels = (
  updatedCourseLevels,
  academicRecords,
  added,
  updatedCourseCodes
) => {
  const mergedUpdates = {};

  _.forEach(updatedCourseLevels, studentLevels => {
    _.forEach(studentLevels, (levelId, compositeKey) => {
      mergedUpdates[compositeKey] = { levelId };
    });
  });

  _.forEach(updatedCourseCodes, studentCourseCodes => {
    _.forEach(studentCourseCodes, (courseCode, compositeKey) => {
      if (!mergedUpdates[compositeKey]) {
        mergedUpdates[compositeKey] = {};
      }
      mergedUpdates[compositeKey].courseCode = courseCode;
    });
  });

  _.forEach(mergedUpdates, (update, key) => {
    const [gradeMapId, courseId] = parseCompositeKey(key);
    const academicRecord = _.find(academicRecords, { id: gradeMapId });
    if (!academicRecord) return;

    const courseItem = _.find(
      academicRecord.courses,
      c => c.course.id === courseId
    );
    if (!courseItem) return;

    const nextLevelId = _.has(update, "levelId")
      ? update.levelId
      : _.get(courseItem, "level.id");
    const nextCourseCode = _.has(update, "courseCode")
      ? update.courseCode
      : _.get(courseItem, "courseCode");

    const nextPayload = {
      studentGradeMapId: gradeMapId,
      courseId,
      displaySequence: courseItem.displaySequence,
      ...(typeof nextLevelId !== "undefined" ? { levelId: nextLevelId } : {}),
      ...(typeof nextCourseCode !== "undefined"
        ? { courseCode: nextCourseCode }
        : {}),
    };

    added.push(nextPayload);
  });
};

const processNewCourseRatingsForRow = ({
  rowId,
  courseId,
  gradeMapId,
  studentRatings,
  input,
}) => {
  _.forEach(studentRatings, (value, compositeKey) => {
    const [ratingRowId, gradingPeriodId, criteriaId] = parseCompositeKey(
      compositeKey
    );
    if (ratingRowId === rowId && !_.isEmpty(_.trim(value))) {
      input.push({
        studentGradeMapId: gradeMapId,
        gradingPeriodId,
        courseId,
        gradeScaleType: "NORMAL",
        gradeScaleId: criteriaId,
        value: _.trim(value),
      });
    }
  });
};

const processNewCourseRatings = (newCourseRows, newCourseRatings, input) => {
  _.forEach(newCourseRows, (studentRows, studentId) => {
    const studentRatings = _.get(newCourseRatings, studentId, {});
    _.forEach(studentRows, (gradeMapRows, gradeMapId) => {
      _.forEach(gradeMapRows, (rowData, rowId) => {
        const { courseId } = rowData;
        if (!courseId) return;

        processNewCourseRatingsForRow({
          rowId,
          courseId,
          gradeMapId,
          studentRatings,
          input,
        });
      });
    });
  });
};

// Thunks / Mutation Handlers
export const updateStudentPersonalDetailsMutationHandler = ({ studentId }) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const { studentPersonalDetails } = _.get(
        state,
        "transcriptV3.transcriptEditGrades",
        {}
      );

      const updatedDetails = transformStudentPersonalDetailsToBackendFormat({
        studentPersonalDetails,
        studentId,
      });

      if (_.isEmpty(updatedDetails)) {
        return;
      }

      await client.mutate({
        mutation: updateStudentPersonalDetailsMutation,
        variables: { input: updatedDetails },
        refetchQueries: ["getStudentPersonalDetails"],
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "transcript:student_personal_details",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateStudentTranscriptCommentMutationHandler = ({
  commentFieldId,
}) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const { studentTranscriptComments = {} } = _.get(
        state,
        "transcriptV3.transcriptEditGrades",
        {}
      );

      const input = Object.entries(studentTranscriptComments).map(
        ([studentId, comment]) => ({
          studentId,
          commentFieldId,
          value: comment,
        })
      );

      if (_.isEmpty(input)) {
        return;
      }

      await client.mutate({
        mutation: upsertTranscriptStudentCommentsMutation,
        variables: { input },
        refetchQueries: ["getStudentTranscriptComment"],
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "transcript:student_transcript_comment",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateStudentGradeCourseMappingMutationHandler = ({
  academicRecords = [],
}) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const {
        deletedRecordRows = {},
        newCourseRows = {},
        updatedCourseLevels = {},
        updatedCourseCodes = {},
      } = _.get(state, "transcriptV3.transcriptEditGrades", {});

      if (_.isEmpty(academicRecords)) {
        return;
      }

      const removed = extractRemovedCourses(deletedRecordRows);

      const added = [];
      const gradeMapIdToRecordMap = _.keyBy(academicRecords, "id");

      _.forEach(newCourseRows, (studentRows, studentId) => {
        _.forEach(studentRows, (gradeMapRows, gradeMapId) => {
          const academicRecord = gradeMapIdToRecordMap[gradeMapId];
          const deletedCourseIds = getDeletedCourseIdsForGradeMap(
            deletedRecordRows,
            studentId,
            gradeMapId
          );

          processNewCourseRowsForGradeMap({
            gradeMapRows,
            gradeMapId,
            academicRecord,
            deletedCourseIds,
            added,
          });
        });
      });

      processUpdatedCourseLevels(
        updatedCourseLevels,
        academicRecords,
        added,
        updatedCourseCodes
      );

      if (_.isEmpty(removed) && _.isEmpty(added)) {
        return;
      }

      const input = { removed, added };

      await client.mutate({
        mutation: updateTranscriptStudentGradeCourseMappingMutation,
        variables: { input },
        refetchQueries: [
          getStudentTranscriptAcademicRecordsQuery,
          getTranscriptResourcesQuery,
        ],
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "transcript:course_mappings",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const upsertStudentRatingsMutationHandler = () => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const {
        studentGradeRecords = {},
        newCourseRows = {},
        newCourseRatings = {},
      } = _.get(state, "transcriptV3.transcriptEditGrades", {});

      const input = [];

      // Process existing course ratings
      _.forEach(studentGradeRecords, studentRecords => {
        _.forEach(studentRecords, record => {
          const {
            gradeMapId,
            courseId,
            gradingPeriodId,
            criteriaId,
            value,
          } = record;

          input.push({
            studentGradeMapId: gradeMapId,
            gradingPeriodId,
            courseId,
            gradeScaleType: "NORMAL",
            gradeScaleId: criteriaId,
            value: _.trim(value),
          });
        });
      });

      // Process new course ratings
      processNewCourseRatings(newCourseRows, newCourseRatings, input);

      if (_.isEmpty(input)) {
        return;
      }

      await client.mutate({
        mutation: upsertTranscriptStudentRatingsMutation,
        variables: { input },
        refetchQueries: [
          getStudentTranscriptAcademicRecordsQuery,
          getTranscriptResourcesQuery,
        ],
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "transcript:student_ratings",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateTranscriptGradeScalesMutationHandler = ({
  input,
  onCompleted,
  onFinally,
}) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: updateTranscriptGradeScalesMutation,
        variables: { input },
        refetchQueries: [getOrgTranscriptEntitiesQuery],
        awaitRefetchQueries: true,
      });

      if (onCompleted) {
        onCompleted();
      }

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "common:grade_scales",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } finally {
      if (onFinally) {
        onFinally();
      }
    }
  };
};

export const syncPlatformGradeScaleForTranscripts = ({
  platformGradeScaleIds,
  onCompleted,
  onError,
}) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: syncPlatformGradeScaleForTranscriptsMutation,
        variables: {
          input: {
            platformGradeScaleIds,
          },
        },
        refetchQueries: [getOrgTranscriptEntitiesQuery],
        awaitRefetchQueries: true,
      });

      const gradeScales = _.get(
        response,
        "data.transcript.syncPlatformGradeScaleForTranscripts.gradeScales",
        []
      );
      const isSuccess = _.get(
        response,
        "data.transcript.syncPlatformGradeScaleForTranscripts.isSuccess",
        false
      );
      const warning = _.get(
        response,
        "data.transcript.syncPlatformGradeScaleForTranscripts.warning"
      );

      if (onCompleted) {
        onCompleted({
          gradeScales,
          isSuccess,
          warning,
        });
      }

      if (warning) {
        dispatch(setToastMsg(warning));
      }
    } catch (error) {
      if (onError) {
        onError(error);
      }
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const bulkCopyTranscriptConfigurationHandler = ({
  sourceStudentId,
  targetStudentIds = [],
  configurationPayload,
  onCompleted,
  onFinally,
}) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: bulkCopyTranscriptConfigurationMutation,
        variables: {
          input: {
            sourceStudentId,
            targetStudentIds,
            configurationPayload,
          },
        },
        refetchQueries: [getStudentTranscriptAcademicRecordsQuery],
        awaitRefetchQueries: true,
      });

      const isSuccess = _.get(
        response,
        "data.transcript.bulkCopyTranscriptConfiguration.isSuccess",
        false
      );
      const warning = _.get(
        response,
        "data.transcript.bulkCopyTranscriptConfiguration.warning"
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:entity_updated_successfully",
            locale_params: [
              {
                key: "entity",
                value: "common:display_settings",
                isPlainText: false,
              },
            ],
            type: "success",
          })
        );

        if (onCompleted) {
          onCompleted({ isSuccess });
        }
      } else if (warning) {
        dispatch(setToastMsg(warning));
      }
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } finally {
      if (onFinally) {
        onFinally();
      }
    }
  };
};

export const updateTranscriptConfigurationHandler = ({
  input,
  onCompleted,
  onFinally,
}) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: updateTranscriptConfigurationMutation,
        variables: {
          input,
        },
        refetchQueries: [
          getStudentTranscriptAcademicRecordsQuery,
          getOrgTranscriptEntitiesQuery,
        ],
        awaitRefetchQueries: true,
      });

      const isSuccess = _.get(
        response,
        "data.transcript.updateTranscriptConfiguration.isSuccess",
        false
      );
      const warning = _.get(
        response,
        "data.transcript.updateTranscriptConfiguration.warning"
      );

      if (isSuccess) {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:entity_updated_successfully",
            locale_params: [
              {
                key: "entity",
                value: "common:display_settings",
                isPlainText: false,
              },
            ],
            type: "success",
          })
        );

        if (onCompleted) {
          onCompleted({ isSuccess });
        }
      } else if (warning) {
        dispatch(setToastMsg(warning));
      }
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } finally {
      if (onFinally) {
        onFinally();
      }
    }
  };
};

export const updateStudentGradeGradingPeriodMappingHandler = ({ input }) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: updateTranscriptStudentGradeGradingPeriodMappingMutation,
        variables: { input },
        refetchQueries: [getStudentTranscriptAcademicRecordsQuery],
        awaitRefetchQueries: true,
      });

      const isSuccess = _.get(
        response,
        "data.transcript.updateTranscriptStudentGradeGradingPeriodMapping.isSuccess",
        false
      );

      return { isSuccess };
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      return { isSuccess: false };
    }
  };
};

export const bulkUpdateStudentGradeGradingPeriodMappingHandler = ({
  sourceStudentId,
  targetStudentIds = [],
}) => {
  return async dispatch => {
    try {
      const response = await client.mutate({
        mutation: bulkCopyTranscriptGradingPeriodsMutation,
        variables: {
          sourceStudentId,
          targetStudentIds,
        },
        refetchQueries: [getStudentTranscriptAcademicRecordsQuery],
        awaitRefetchQueries: true,
      });

      const isSuccess = _.get(
        response,
        "data.transcript.bulkCopyTranscriptGradingPeriods.isSuccess",
        false
      );
      const warning = _.get(
        response,
        "data.transcript.bulkCopyTranscriptGradingPeriods.warning"
      );

      if (!isSuccess && warning) {
        dispatch(setToastMsg(warning));
      }
      return { isSuccess };
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
      return { isSuccess: false };
    }
  };
};

export const updateTranscriptGradingPeriodsHandler = ({
  input,
  onCompleted,
  onFinally,
}) => {
  return async dispatch => {
    try {
      await client.mutate({
        mutation: updateTranscriptGradingPeriodsMutation,
        variables: { input },
        refetchQueries: [
          getOrgTranscriptEntitiesQuery,
          getStudentTranscriptAcademicRecordsQuery,
        ],
        awaitRefetchQueries: true,
      });

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "common:grading_period_plural",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );

      if (onCompleted) {
        onCompleted();
      }
    } catch (error) {
      if (_.get(error, "networkError")) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    } finally {
      if (onFinally) {
        onFinally();
      }
    }
  };
};
